import { apiClient } from './client'
import type {
  LLMProvider,
  LLMProviderCreatePayload,
  LLMProviderTestResponse,
  LLMProviderUpdatePayload,
  OrganizationLLMConfig,
  OrganizationLLMCredentialPayload,
  OrganizationLLMProfile,
  OrganizationLLMProvider,
  OrganizationLLMProviderModel,
} from './runtimeTypes'

export interface LLMProvidersListResponse {
  llmProviders: LLMProvider[]
}

const providerBasePath = (organizationId: string | number) =>
  `/organization/${organizationId}/llm/providers`

const profilesBasePath = (organizationId: string | number) =>
  `/organization/${organizationId}/llm/profiles`

const deploymentConfigPath = (
  organizationId: string | number,
  deploymentId: string | number,
) => `/organization/${organizationId}/deployments/${deploymentId}/llm-config`

const recipeConfigPath = (
  organizationId: string | number,
  recipeId: string | number,
) => `/organization/${organizationId}/workflows/${recipeId}/llm-config`

export interface DeploymentLLMConfigPayload {
  default_profile_id?: string | null
  run_profile_id?: string | null
  build_profile_id?: string | null
  embedding_provider_id?: string | null
}

export interface DeploymentLLMRoleSelectionPayload {
  role: 'run' | 'build' | 'embedding'
  provider_family?: string
  model_code?: string
  llm_provider_id?: string
  llm_model_id?: string
  temperature?: number
  max_completion_tokens?: number
}

const toLegacyProvider = (
  provider: OrganizationLLMProvider,
  model?: OrganizationLLMProviderModel,
): LLMProvider => ({
  id: provider.id,
  name: provider.display_name,
  provider: provider.provider_family as LLMProvider['provider'],
  model: model?.model_ref,
  url: provider.base_url,
  apiUrl: provider.base_url,
  is_active: provider.enabled,
  is_default: false,
  organization_id: provider.organization_id,
  created_at: provider.created_at,
  updated_at: provider.updated_at,
})

const familyToAdapter = (provider: string) => {
  switch (provider) {
    case 'anthropic':
      return 'anthropic_messages'
    case 'google':
      return 'google_genai'
    case 'agentmaurice':
      return 'agentmaurice_gateway'
    case 'ollama':
    case 'local':
      return 'ollama'
    case 'openrouter':
    case 'mammouth':
    case 'mistral':
    case 'custom':
    case 'custom_openai_compatible':
      return 'openai_compatible'
    default:
      return 'openai_chat'
  }
}

const normalizeProviderFamily = (provider: string) =>
  provider === 'custom' || provider === 'local'
    ? provider === 'local'
      ? 'ollama'
      : 'custom_openai_compatible'
    : provider

const AGENTMAURICE_PROVIDER_SLUG = 'agentmaurice'
const AGENTMAURICE_DEFAULT_MODEL_REF = 'hosted:gpt-5.1-chat'
const AGENTMAURICE_DEFAULT_MODEL_NAME = 'AgentMaurice GPT-5.1 Chat'
const AGENTMAURICE_DEFAULT_PROFILE_NAME = 'AgentMaurice default'

async function listModelsIgnoringSetupErrors(
  organizationId: string | number,
  providerId: string | number,
) {
  try {
    return await llmAPI.listModels(organizationId, providerId)
  } catch {
    return []
  }
}

async function ensureAgentMauriceModel(
  organizationId: string | number,
  providerId: string | number,
) {
  let models = await listModelsIgnoringSetupErrors(organizationId, providerId)
  let model =
    models.find((item) => item.active && item.model_ref === AGENTMAURICE_DEFAULT_MODEL_REF) ||
    models.find((item) => item.active) ||
    models.find((item) => item.model_ref === AGENTMAURICE_DEFAULT_MODEL_REF) ||
    models[0]

  if (model) {
    return model
  }

  try {
    models = await llmAPI.syncModels(organizationId, providerId)
    model =
      models.find((item) => item.active && item.model_ref === AGENTMAURICE_DEFAULT_MODEL_REF) ||
      models.find((item) => item.active) ||
      models.find((item) => item.model_ref === AGENTMAURICE_DEFAULT_MODEL_REF) ||
      models[0]
    if (model) {
      return model
    }
  } catch {
    // AgentMaurice can be configured before a remote model catalog is available.
  }

  try {
    return await llmAPI.createModel(organizationId, providerId, {
      model_ref: AGENTMAURICE_DEFAULT_MODEL_REF,
      display_name: AGENTMAURICE_DEFAULT_MODEL_NAME,
      context_window: 65536,
      modalities: ['text'],
      supports_tools: true,
      supports_json: true,
      active: true,
    })
  } catch (error) {
    models = await listModelsIgnoringSetupErrors(organizationId, providerId)
    model =
      models.find((item) => item.model_ref === AGENTMAURICE_DEFAULT_MODEL_REF) ||
      models.find((item) => item.active) ||
      models[0]
    if (model) {
      return model
    }
    throw error
  }
}

async function ensureAgentMauriceProfile(
  organizationId: string | number,
  provider: OrganizationLLMProvider,
  model: OrganizationLLMProviderModel,
) {
  const profiles = await llmAPI.listProfiles(organizationId)
  let profile =
    profiles.find(
      (item) =>
        item.provider_id === provider.id &&
        item.model_id === model.id &&
        item.name === AGENTMAURICE_DEFAULT_PROFILE_NAME,
    ) ||
    profiles.find((item) => item.provider_id === provider.id && item.model_id === model.id) ||
    profiles.find((item) => item.provider_family === 'agentmaurice' && item.is_org_default)

  if (!profile) {
    try {
      profile = await llmAPI.createProfile(organizationId, {
        name: AGENTMAURICE_DEFAULT_PROFILE_NAME,
        provider_id: provider.id,
        model_id: model.id,
        temperature: 0.2,
        max_completion_tokens: 4096,
        is_org_default: true,
      })
    } catch (error) {
      const refreshed = await llmAPI.listProfiles(organizationId)
      profile =
        refreshed.find((item) => item.provider_id === provider.id && item.model_id === model.id) ||
        refreshed.find((item) => item.provider_family === 'agentmaurice' && item.is_org_default)
      if (!profile) {
        throw error
      }
    }
  } else if (!profile.is_org_default) {
    profile = await llmAPI.updateProfile(organizationId, profile.id, {
      is_org_default: true,
    })
  }

  return profile
}

export const llmAPI = {
  listOrganizationProviders: (organizationId: string | number) =>
    apiClient.get<OrganizationLLMProvider[]>(providerBasePath(organizationId)),

  getOrganizationProvider: (
    organizationId: string | number,
    providerId: string | number,
  ) =>
    apiClient.get<OrganizationLLMProvider>(
      `${providerBasePath(organizationId)}/${providerId}`,
    ),

  createOrganizationProvider: (
    organizationId: string | number,
    data: {
      slug: string
      display_name: string
      provider_family: string
      adapter: string
      base_url?: string
      enabled?: boolean
      capabilities?: string[]
    },
  ) => apiClient.post<OrganizationLLMProvider>(providerBasePath(organizationId), data),

  updateOrganizationProvider: (
    organizationId: string | number,
    providerId: string | number,
    data: Partial<OrganizationLLMProvider>,
  ) =>
    apiClient.put<OrganizationLLMProvider>(
      `${providerBasePath(organizationId)}/${providerId}`,
      data,
    ),

  deleteOrganizationProvider: (
    organizationId: string | number,
    providerId: string | number,
  ) => apiClient.delete<void>(`${providerBasePath(organizationId)}/${providerId}`),

  upsertCredential: (
    organizationId: string | number,
    providerId: string | number,
    data: OrganizationLLMCredentialPayload,
  ) =>
    apiClient.post<void>(
      `${providerBasePath(organizationId)}/${providerId}/credential`,
      data,
    ),

  testOrganizationProvider: (
    organizationId: string | number,
    providerId: string | number,
  ) =>
    apiClient.post<{ ok: boolean; message?: string }>(
      `${providerBasePath(organizationId)}/${providerId}/test`,
    ),

  syncModels: (organizationId: string | number, providerId: string | number) =>
    apiClient.post<OrganizationLLMProviderModel[]>(
      `${providerBasePath(organizationId)}/${providerId}/sync-models`,
    ),

  listModels: (organizationId: string | number, providerId: string | number) =>
    apiClient.get<OrganizationLLMProviderModel[]>(
      `${providerBasePath(organizationId)}/${providerId}/models`,
    ),

  createModel: (
    organizationId: string | number,
    providerId: string | number,
    data: Partial<OrganizationLLMProviderModel> & { model_ref: string },
  ) =>
    apiClient.post<OrganizationLLMProviderModel>(
      `${providerBasePath(organizationId)}/${providerId}/models`,
      data,
    ),

  createProfile: (
    organizationId: string | number,
    data: Partial<OrganizationLLMProfile> & {
      name: string
      provider_id: string
      model_id: string
    },
  ) => apiClient.post<OrganizationLLMProfile>(profilesBasePath(organizationId), data),

  updateProfile: (
    organizationId: string | number,
    profileId: string | number,
    data: Partial<OrganizationLLMProfile>,
  ) =>
    apiClient.put<OrganizationLLMProfile>(
      `${profilesBasePath(organizationId)}/${profileId}`,
      data,
    ),

  deleteProfile: (organizationId: string | number, profileId: string | number) =>
    apiClient.delete<void>(`${profilesBasePath(organizationId)}/${profileId}`),

  getDeploymentConfig: (
    organizationId: string | number,
    deploymentId: string | number,
  ) => apiClient.get<OrganizationLLMConfig>(deploymentConfigPath(organizationId, deploymentId)),

  setDeploymentConfig: (
    organizationId: string | number,
    deploymentId: string | number,
    profileIdOrConfig: string | number | DeploymentLLMConfigPayload,
  ) =>
    apiClient.put<void>(
      deploymentConfigPath(organizationId, deploymentId),
      typeof profileIdOrConfig === 'object'
        ? profileIdOrConfig
        : { default_profile_id: profileIdOrConfig },
    ),

  setDeploymentRoleConfig: (
    organizationId: string | number,
    deploymentId: string | number,
    data: DeploymentLLMConfigPayload,
  ) => apiClient.put<void>(deploymentConfigPath(organizationId, deploymentId), data),

  selectDeploymentRoleModel: (
    organizationId: string | number,
    deploymentId: string | number,
    data: DeploymentLLMRoleSelectionPayload,
  ) =>
    apiClient.post<OrganizationLLMConfig>(
      `${deploymentConfigPath(organizationId, deploymentId)}/select-model`,
      data,
    ),

  getRecipeConfig: (organizationId: string | number, recipeId: string | number) =>
    apiClient.get<OrganizationLLMConfig>(recipeConfigPath(organizationId, recipeId)),

  setRecipeConfig: (
    organizationId: string | number,
    recipeId: string | number,
    profileId: string | number,
  ) =>
    apiClient.put<void>(recipeConfigPath(organizationId, recipeId), {
      default_profile_id: profileId,
    }),

  list: async (
    organizationId: string | number,
    _deploymentId: string | number,
  ): Promise<LLMProvidersListResponse> => {
    const providers = await apiClient.get<OrganizationLLMProvider[]>(
      providerBasePath(organizationId),
    )
    return { llmProviders: providers.map((provider) => toLegacyProvider(provider)) }
  },

  get: async (
    organizationId: string | number,
    _deploymentId: string | number,
    providerId: string | number,
  ) => {
    const provider = await apiClient.get<OrganizationLLMProvider>(
      `${providerBasePath(organizationId)}/${providerId}`,
    )
    return toLegacyProvider(provider)
  },

  create: async (
    organizationId: string | number,
    deploymentId: string | number,
    data: LLMProviderCreatePayload,
  ) => {
    const providerFamily = normalizeProviderFamily(data.provider)
    const provider = await apiClient.post<OrganizationLLMProvider>(
      providerBasePath(organizationId),
      {
        slug: data.name,
        display_name: data.name,
        provider_family: providerFamily,
        adapter: familyToAdapter(data.provider),
        base_url: data.url,
        enabled: true,
      },
    )

    if (data.apiKey) {
      await apiClient.post<void>(
        `${providerBasePath(organizationId)}/${provider.id}/credential`,
        { source: 'user_byok', secret: data.apiKey },
      )
    }

    let model: OrganizationLLMProviderModel | undefined
    if (data.model) {
      model = await apiClient.post<OrganizationLLMProviderModel>(
        `${providerBasePath(organizationId)}/${provider.id}/models`,
        {
          model_ref: data.model,
          display_name: data.model,
          active: true,
        },
      )
    }

    if (model) {
      const profile = await apiClient.post<OrganizationLLMProfile>(
        profilesBasePath(organizationId),
        {
          name: `${data.name} / ${data.model}`,
          provider_id: provider.id,
          model_id: model.id,
          system_prompt_override: data.systemPrompt,
          max_completion_tokens: data.maxCompletionTokens,
          is_org_default: Boolean(data.is_default),
        },
      )
      if (data.is_default) {
        await apiClient.put<void>(
          `/organization/${organizationId}/deployments/${deploymentId}/llm-config`,
          { default_profile_id: profile.id },
        )
      }
    }

    return toLegacyProvider(provider, model)
  },

  update: async (
    organizationId: string | number,
    _deploymentId: string | number,
    providerId: string | number,
    data: LLMProviderUpdatePayload,
  ) => {
    const payload: Record<string, unknown> = {}
    if (data.name) {
      payload.display_name = data.name
      payload.slug = data.name
    }
    if (data.url !== undefined) payload.base_url = data.url
    const provider = await apiClient.put<OrganizationLLMProvider>(
      `${providerBasePath(organizationId)}/${providerId}`,
      payload,
    )
    if (data.apiKey) {
      await apiClient.post<void>(
        `${providerBasePath(organizationId)}/${providerId}/credential`,
        { source: 'user_byok', secret: data.apiKey },
      )
    }
    return toLegacyProvider(provider)
  },

  delete: (
    organizationId: string | number,
    _deploymentId: string | number,
    providerId: string | number,
  ) => apiClient.delete<void>(`${providerBasePath(organizationId)}/${providerId}`),

  test: async (
    organizationId: string | number,
    _deploymentId: string | number,
    providerId: string | number,
  ) => {
    const response = await apiClient.post<{ ok: boolean; message?: string }>(
      `${providerBasePath(organizationId)}/${providerId}/test`,
    )
    return {
      success: response.ok,
      ok: response.ok,
      message: response.message || '',
    } satisfies LLMProviderTestResponse
  },

  listProfiles: (organizationId: string | number) =>
    apiClient.get<OrganizationLLMProfile[]>(profilesBasePath(organizationId)),

  setDeploymentProfile: (
    organizationId: string | number,
    deploymentId: string | number,
    profileId: string | number,
  ) =>
    llmAPI.setDeploymentConfig(organizationId, deploymentId, profileId),

  ensureAgentMauriceDefaultProfile: async (
    organizationId: string | number,
    deploymentId: string | number,
  ) => {
    const providers = await llmAPI.listOrganizationProviders(organizationId)
    let provider =
      providers.find((item) => item.provider_family === 'agentmaurice') ||
      providers.find((item) => item.slug === AGENTMAURICE_PROVIDER_SLUG)

    if (!provider) {
      provider = await llmAPI.createOrganizationProvider(organizationId, {
        slug: AGENTMAURICE_PROVIDER_SLUG,
        display_name: 'AgentMaurice',
        provider_family: 'agentmaurice',
        adapter: 'agentmaurice_gateway',
        enabled: true,
        capabilities: ['chat', 'tools', 'json'],
      })
    } else if (!provider.enabled) {
      provider = await llmAPI.updateOrganizationProvider(organizationId, provider.id, {
        enabled: true,
      })
    }

    const model = await ensureAgentMauriceModel(organizationId, provider.id)
    const profile = await ensureAgentMauriceProfile(organizationId, provider, model)
    await llmAPI.setDeploymentRoleConfig(organizationId, deploymentId, {
      run_profile_id: profile.id,
      build_profile_id: profile.id,
    })

    return { provider, model, profile }
  },
}
